import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import api from '@/api'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    currentBook: null,
    chapters: [],
    currentChapter: null,
    fontSize: 16,
    theme: localStorage.getItem('theme') || 'system',
    user: null,
    token: localStorage.getItem('token'),
    audioPlayer: {
      book: null, tracks: [], trackIndex: 0, currentTime: 0, duration: 0,
      playbackRate: 1, isPlaying: false, buffering: false, error: '', sleepMinutes: 0
    },
    userSettings: {
      fontSize: 18,
      pageWidth: 800,
      readingMode: 'scroll',
      showOriginalText: false,
      theme: 'theme-default',
      lineHeight: '1.8',
      fontFamily: 'default',
      brightness: 100
    }
  },

  mutations: {
    setBook(state, book) {
      state.currentBook = book
    },
    setChapters(state, chapters) {
      state.chapters = chapters
    },
    setCurrentChapter(state, chapter) {
      state.currentChapter = chapter
    },
    setFontSize(state, size) {
      state.fontSize = size
    },
    setTheme(state, theme) {
      state.theme = theme;
      localStorage.setItem('theme', theme);
      if (theme === 'system') document.documentElement.removeAttribute('data-theme');
      else document.documentElement.setAttribute('data-theme', theme);
    },
    setUserSettings(state, settings) {
      state.userSettings = { ...state.userSettings, ...settings };
    },
    updateUserSetting(state, { key, value }) {
      state.userSettings = { ...state.userSettings, [key]: value };
    },
    setUser(state, user) {
      state.user = user;
    },
    setToken(state, token) {
      state.token = token;
      localStorage.setItem('token', token);
    },
    setAudioSession(state, session) {
      state.audioPlayer = {
        ...state.audioPlayer, ...session, isPlaying: false, buffering: false, error: ''
      }
    },
    updateAudioPlayer(state, values) {
      state.audioPlayer = { ...state.audioPlayer, ...values }
    },
    clearAudioPlayer(state) {
      state.audioPlayer = {
        book: null, tracks: [], trackIndex: 0, currentTime: 0, duration: 0,
        playbackRate: 1, isPlaying: false, buffering: false, error: '', sleepMinutes: 0
      }
    },
    logout(state) {
      state.user = null;
      state.token = null;
      localStorage.removeItem('token');
      state.audioPlayer = {
        book: null, tracks: [], trackIndex: 0, currentTime: 0, duration: 0,
        playbackRate: 1, isPlaying: false, buffering: false, error: '', sleepMinutes: 0
      };
    }
  },

  actions: {
    async loadAudioBook({ state, commit }, bookId) {
      if (state.audioPlayer.book && Number(state.audioPlayer.book.id) === Number(bookId) && state.audioPlayer.tracks.length) {
        return state.audioPlayer
      }
      const previous = state.audioPlayer
      const previousTrack = previous.tracks && previous.tracks[previous.trackIndex]
      if (previous.book && previousTrack && state.token && Number(previous.currentTime) > 0) {
        let overallPosition = 0
        let canCalculateOverall = true
        for (let index = 0; index < previous.trackIndex; index++) {
          const duration = Number(previous.tracks[index].durationSeconds)
          if (!Number.isFinite(duration) || duration <= 0) { canCalculateOverall = false; break }
          overallPosition += duration
        }
        if (canCalculateOverall) overallPosition += Number(previous.currentTime) || 0
        try {
          await api.progress.saveReadingProgress({
            bookId: previous.book.id, mediaType: 'AUDIO', chapterId: null,
            trackId: previousTrack.id, positionSeconds: Number(previous.currentTime),
            currentTrackDurationSeconds: Number(previousTrack.durationSeconds) || null,
            totalDurationSeconds: Number(previous.book.totalDurationSeconds) || null,
            overallPositionSeconds: canCalculateOverall ? overallPosition : null,
            trackIndex: previous.trackIndex, totalTracks: previous.tracks.length,
            trackTitle: previousTrack.title, readingMode: 'audio'
          })
        } catch (error) { /* keep switching even if the previous progress save fails */ }
      }
      const { data: book } = await api.book.getBookDetail(bookId)
      if (book.mediaType !== 'AUDIO') throw new Error('这项内容不是有声书')
      const [{ data: tracks }, { data: progress }] = await Promise.all([
        api.book.getAudioTracks(bookId),
        api.progress.getReadingProgress(bookId).catch(() => ({ data: null }))
      ])
      if (!tracks || !tracks.length) throw new Error('这本有声书没有可播放的音轨')
      const savedIndex = progress && progress.completed
        ? 0 : Math.max(0, tracks.findIndex(track => Number(track.id) === Number(progress && progress.trackId)))
      const current = tracks[savedIndex]
      commit('setAudioSession', {
        book, tracks, trackIndex: savedIndex,
        currentTime: progress && !progress.completed && Number(progress.trackId) === Number(current.id) ? Number(progress.positionSeconds) || 0 : 0,
        duration: Number(current.durationSeconds) || 0
      })
      return state.audioPlayer
    },
    async loadBook({ commit }, bookId) {
      try {
        const { data } = await axios.get(`/api/books/${bookId}`)
        commit('setBook', data)
      } catch (error) {
        console.error('加载书籍失败:', error)
      }
    },

    async loadChapters({ commit }, bookId) {
      try {
        const { data } = await axios.get(`/api/books/${bookId}/chapters`)
        commit('setChapters', data)
      } catch (error) {
        console.error('加载章节列表失败:', error)
      }
    },

    async loadChapter({ commit }, chapterId) {
      try {
        const { data } = await axios.get(`/api/chapters/${chapterId}`)
        commit('setCurrentChapter', data)
      } catch (error) {
        console.error('加载章节内容失败:', error)
      }
    },

    async initUserState({ commit }) {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          const { data } = await api.user.getUserInfo()
          commit('setUser', data)
          // 拉取用户设置
          try {
            const { data: settings } = await api.settings.getUserSettings()
            commit('setUserSettings', settings)
          } catch (e) {
            // ignore settings load error
          }
        } catch (error) {
          commit('logout')
        }
      }
      // 未登录时，从本地回退加载设置
      if (!token) {
        const savedFontSize = localStorage.getItem('reader-font-size')
        const savedMode = localStorage.getItem('reading-mode')
        const savedOriginal = localStorage.getItem('show-original-text')
        const savedTheme = localStorage.getItem('reader-theme')
        const savedLineHeight = localStorage.getItem('reader-line-height')
        const savedFontFamily = localStorage.getItem('reader-font-family')
        const savedPageWidth = localStorage.getItem('reader-page-width')
        commit('setUserSettings', {
          fontSize: savedFontSize ? parseInt(savedFontSize) : 18,
          pageWidth: savedPageWidth ? parseInt(savedPageWidth) : 800,
          readingMode: savedMode || 'scroll',
          showOriginalText: savedOriginal === 'true' ? true : false,
          theme: savedTheme || 'theme-default',
          lineHeight: savedLineHeight || '1.8',
          fontFamily: savedFontFamily || 'default'
        })
      }
      // 亮度始终走本地（后端无对应列），登录与否都从 localStorage 回填
      const savedBrightness = localStorage.getItem('reader-brightness')
      if (savedBrightness !== null) {
        commit('updateUserSetting', { key: 'brightness', value: parseInt(savedBrightness) })
      }
    },
    async initUserSettings({ dispatch }) {
      // 保留方法名以符合计划，但已并入 initUserState
      return dispatch('initUserState')
    },
    async updateUserSetting({ state, commit }, { key, value }) {
      const prev = state.userSettings && state.userSettings[key]
      if (prev === value) {
        return
      }
      commit('updateUserSetting', { key, value })
      // 亮度仅本地持久化，不入库（后端无对应列）
      if (key === 'brightness') {
        localStorage.setItem('reader-brightness', String(value))
        return
      }
      const token = state.token
      try {
        if (token) {
          await api.settings.updateUserSettings({ [key]: value })
        } else {
          // 未登录写入本地
          if (key === 'fontSize') localStorage.setItem('reader-font-size', String(value))
          if (key === 'pageWidth') localStorage.setItem('reader-page-width', String(value))
          if (key === 'readingMode') localStorage.setItem('reading-mode', String(value))
          if (key === 'showOriginalText') localStorage.setItem('show-original-text', String(value))
          if (key === 'theme') localStorage.setItem('reader-theme', String(value))
          if (key === 'lineHeight') localStorage.setItem('reader-line-height', String(value))
          if (key === 'fontFamily') localStorage.setItem('reader-font-family', String(value))
        }
      } catch (e) {
        // 回滚（简单回滚：不处理旧值缓存，真实场景可存快照）
      }
    }
  }
})
